// OrderBook.jsx
import React, { useEffect, useState, useRef } from "react";

/**
 * Универсальный компонент стакана (order book)
 *
 * Props:
 * - symbol: строка пары, например "USDC/EUR"
 * - depth: число уровней, например 25
 * - wsUrl: URL WebSocket для подключения
 * - format: "kraken" | "binance" — определяет как парсить данные
 */
const OrderBook = ({ symbol = "USDC/EUR", depth = 25, wsUrl, format = "kraken" }) => {
  const [bids, setBids] = useState([]);
  const [asks, setAsks] = useState([]);
  const [connected, setConnected] = useState(false);

  // Статистика
  const [msgCount, setMsgCount] = useState(0);
  const [dataVolume, setDataVolume] = useState(0);       // общий объём данных (байты)
  const [msgPerSec, setMsgPerSec] = useState(0);         // сообщений в секунду
  const [kbPerSec, setKbPerSec] = useState(0);           // скорость данных в КБ/сек

  // вспомогательные счетчики
  const msgCounterRef = useRef(0);
  const volumeCounterRef = useRef(0);

  useEffect(() => {
    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    const wsFullUrl = wsUrl || `${protocol}://${window.location.host}/api/crypto-node/kraken_depth?symbol=${symbol}&depth=${depth}`;
    const ws = new WebSocket(wsFullUrl);

    ws.onopen = () => {
      console.log(`🟢 WS connected: ${symbol}`);
      setConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        let obBids = [];
        let obAsks = [];

        if (format === "kraken") {
          if (data.data) {
            const ob = Array.isArray(data.data) ? data.data[0] : data.data;
            obBids = ob.bids || [];
            obAsks = ob.asks || [];
          } else {
            // fallback на старый стиль
            obBids = data.bids || [];
            obAsks = data.asks || [];
          }
        } else if (format === "binance") {
          obBids = data.bids || [];
          obAsks = data.asks || [];
        }

        setBids(obBids);
        setAsks(obAsks);

        // === Статистика ===
        const rawSize = event.data.length; // длина строки в байтах
        msgCounterRef.current += 1;
        volumeCounterRef.current += rawSize;
        setMsgCount(msgCounterRef.current);
        setDataVolume(volumeCounterRef.current);
      } catch (err) {
        console.error("❌ Error parsing WS message:", err);
      }
    };

    ws.onerror = (err) => console.error("❌ WS error", err);
    ws.onclose = () => {
      console.log(`🔴 WS disconnected: ${symbol}`);
      setConnected(false);
    };

    // Обновление статистики каждую секунду
    const interval = setInterval(() => {
      // Сообщения в секунду
      setMsgPerSec(msgCounterRef.current);

      // Объём данных в КБ/сек
      setKbPerSec((volumeCounterRef.current / 1024).toFixed(2));

      // сбрасываем счетчики на следующий интервал
      msgCounterRef.current = 0;
      volumeCounterRef.current = 0;
    }, 1000);

    return () => {
      ws.close();
      clearInterval(interval);
    };
  }, [symbol, depth, wsUrl, format]);

  return (
    <div style={{ padding: 20, fontFamily: "Arial, sans-serif", border: "1px solid #ccc", borderRadius: 8 }}>
      <h2>📊 {symbol} Order Book ({format})</h2>
      <p>Status: {connected ? "🟢 Live" : "🔴 Disconnected"}</p>
      <p>Messages/sec: {msgPerSec}</p>
      <p>Total messages: {msgCount}</p>
      <p>Total data received: {(dataVolume / 1024).toFixed(2)} KB</p>
      <p>Data flow: {kbPerSec} KB/sec</p>

      <div style={{ display: "flex", gap: 40 }}>
        {/* BIDS */}
        <div style={{ minWidth: 200 }}>
          <h3 style={{ color: "#00c853" }}>Bids</h3>
          {bids.length === 0 && <p>Нет данных</p>}
          {bids.map((b, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between" }}>
              <span>{b.price.toFixed(5)}</span>
              <span>{b.qty.toLocaleString()}</span>
            </div>
          ))}
        </div>

        {/* ASKS */}
        <div style={{ minWidth: 200 }}>
          <h3 style={{ color: "#ff5252" }}>Asks</h3>
          {asks.length === 0 && <p>Нет данных</p>}
          {asks.map((a, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between" }}>
              <span>{a.price.toFixed(5)}</span>
              <span>{a.qty.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OrderBook;

